(() => {
  const CONFIG = {
    EPS: 0.75,                 // edge jitter tolerance (px)
    PAD_PX: 1,                 // widen sticky range by +/- px to avoid rounding issues
    SNAP_DELAY_MS: 120,        // delay after last scroll (trackpad/drag) before snapping
    THRESH_RATIO: 0.12,        // 12% of viewport width to decide slide change on smooth scroll
    THRESH_MIN_PX: 24,         // minimum px threshold for slide change
    USER_SNAP_WINDOW_MS: 450,  // only snap if a recent horizontal interaction occurred
    // Wheel (discrete mouse wheel)
    WHEEL_SNAP_COOLDOWN_MS: 180, // prevent double-fire between ticks
    WHEEL_DISCRETE_THRESHOLD: 80, // px: consider as a "tick" if |delta| >= this
    // Animation
    SNAP_MS: 320,              // default snap duration (ms)
    SNAP_MS_FIRST: 420,        // longer duration when snapping to the first slide for extra smoothness
    EASE: 'easeOutCubic'       // easing: easeOutCubic | easeOutQuart | easeOutQuint
  };

  // Easing functions
  const EASE = {
    easeOutCubic: t => 1 - Math.pow(1 - t, 3),
    easeOutQuart: t => 1 - Math.pow(1 - t, 4),
    easeOutQuint: t => 1 - Math.pow(1 - t, 5)
  };

  class ScrollStickyContainer extends HTMLElement {
    constructor() {
      super();

      // Bind
      this._measure = this._measure.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onWheel = this._onWheel.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this._collectItems = this._collectItems.bind(this);
      this._scheduleSnap = this._scheduleSnap.bind(this);
      this._snapBySession = this._snapBySession.bind(this);
      this._snapToIndex = this._snapToIndex.bind(this);
      this._nearestIndex = this._nearestIndex.bind(this);
      this._markUserHScroll = this._markUserHScroll.bind(this);
      this._animateScrollLeft = this._animateScrollLeft.bind(this);

      // State
      this._rafId = null;
      this._isProgrammatic = false;
      this._isDragging = false;

      this._sessionActive = false;
      this._sessionStartLeft = 0;
      this._sessionStartIndex = 0;

      this._activeIndex = 0;
      this._lastUserHScrollTS = 0;
      this._lastWheelSnapTS = 0;

      this._dragStartX = 0;
      this._dragStartScrollLeft = 0;

      this._snapTimer = null;
      this._snapLock = false;

      this._animFrame = null;       // rAF id for horizontal animation
      this._animStart = 0;          // animation start timestamp
      this._animFrom = 0;           // start scrollLeft
      this._animTo = 0;             // target scrollLeft
      this._animDur = 0;            // duration ms
      this._animEase = EASE[CONFIG.EASE] || EASE.easeOutCubic;

      // Elements / data
      this._main = null;
      this._items = [];
      this._itemStarts = []; // cached offsetLeft per slide

      // Observers
      this._resizeObserver = null;
      this._mutationObserver = null;

      // Metrics
      this._metrics = {
        containerTop: 0,
        containerHeight: 0,
        viewportH: 0,
        scrollStart: 0,
        scrollEnd: 0,
        mainMaxScrollLeft: 0
      };
    }

    connectedCallback() {
      this._main = this.querySelector('.main-scroll-horizontally');
      if (!this._main) return;

      if (!this._main.style.overflowX) this._main.style.overflowX = 'auto';
      this._main.style.willChange = 'scroll-position';
      // important: use JS-driven animation, avoid native smooth to prevent conflicts
      this._main.style.scrollBehavior = 'auto';
      if (!this._main.style.cursor) this._main.style.cursor = 'grab';

      this._measure();

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._measure, { passive: true });
      this.addEventListener('wheel', this._onWheel, { passive: false });

      this._main.addEventListener('pointerdown', this._onPointerDown, { passive: true });
      this._main.addEventListener('scroll', this._scheduleSnap, { passive: true });

      this._resizeObserver = new ResizeObserver(this._measure);
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this._main);

      this._mutationObserver = new MutationObserver(this._measure);
      this._mutationObserver.observe(this._main, { childList: true, subtree: true });

      this._updateSpacerHeight();

      this._activeIndex = this._nearestIndex(this._main.scrollLeft);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._measure);
      this.removeEventListener('wheel', this._onWheel);
      this._main?.removeEventListener('pointerdown', this._onPointerDown);
      this._main?.removeEventListener('scroll', this._scheduleSnap);

      this._resizeObserver?.disconnect();
      this._mutationObserver?.disconnect();

      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._snapTimer) clearTimeout(this._snapTimer);
      if (this._animFrame) cancelAnimationFrame(this._animFrame);
    }

    // ===== Helpers =====
    _markUserHScroll() {
      this._lastUserHScrollTS = performance.now ? performance.now() : Date.now();
    }

    _collectItems() {
      if (!this._main) return;
      this._items = Array.from(this._main.querySelectorAll('slide-item, [data-snap]'));
      this._itemStarts = this._items.map(el => el.offsetLeft);
    }

    _nearestIndex(left) {
      if (!this._items.length) return 0;
      const viewCenter = left + (this._main.clientWidth || 1) / 2;
      let best = 0, bestDist = Infinity;
      for (let i = 0; i < this._items.length; i++) {
        const el = this._items[i];
        const w = el.getBoundingClientRect().width;
        const center = (this._itemStarts[i] ?? el.offsetLeft) + w / 2;
        const d = Math.abs(center - viewCenter);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    _leftOfIndex(index) {
      if (this._items[index]) return this._itemStarts[index] ?? this._items[index].offsetLeft;
      const vw = this._main.clientWidth || 1;
      return index * vw;
    }

    // rAF-driven horizontal animation + vertical mapping sync throughout the animation
    _animateScrollLeft(toLeft, durationMs) {
      if (!this._main) return;

      // cancel any running animation
      if (this._animFrame) cancelAnimationFrame(this._animFrame);

      const max = Math.max(0, this._main.scrollWidth - this._main.clientWidth);
      const rect = this.getBoundingClientRect();
      const pageY = window.scrollY || document.documentElement.scrollTop;
      const containerTop = pageY + rect.top;
      const containerHeight = this.offsetHeight;
      const viewportH = window.innerHeight;
      const scrollStart = containerTop;
      const scrollEnd = containerTop + containerHeight - viewportH;

      this._animStart = performance.now ? performance.now() : Date.now();
      this._animFrom = this._main.scrollLeft;
      this._animTo = Math.max(0, Math.min(toLeft, max));
      this._animDur = Math.max(60, durationMs | 0); // at least one frame

      const tick = (nowTs) => {
        const now = nowTs || (performance.now ? performance.now() : Date.now());
        const t = Math.min(1, (now - this._animStart) / this._animDur);
        const k = this._animEase(t);

        const current = this._animFrom + (this._animTo - this._animFrom) * k;

        this._isProgrammatic = true;
        this._main.scrollLeft = current;

        // Keep vertical mapping synced to avoid jitter
        if (max > 0) {
          const progress = current / max;
          const mappedY = scrollStart + progress * (scrollEnd - scrollStart);
          const clampedY = Math.max(scrollStart, Math.min(mappedY, scrollEnd));
          if (Math.abs((window.scrollY || document.documentElement.scrollTop) - clampedY) > 0.5) {
            window.scrollTo({ top: clampedY });
          }
        }

        if (t < 1) {
          this._animFrame = requestAnimationFrame(tick);
        } else {
          this._isProgrammatic = false;
          this._animFrame = null;
          // ensure exact final value
          this._main.scrollLeft = this._animTo;
        }
      };

      this._animFrame = requestAnimationFrame(tick);
    }

    // ===== Measurements =====
    _measure = () => {
      if (!this._main) return;

      const rect = this.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const containerTop = scrollTop + rect.top;
      const containerHeight = this.offsetHeight;
      const viewportH = window.innerHeight;

      const scrollStart = containerTop;
      const scrollEnd = containerTop + containerHeight - viewportH;
      const mainMaxScrollLeft = Math.max(0, this._main.scrollWidth - this._main.clientWidth);

      this._metrics = {
        containerTop,
        containerHeight,
        viewportH,
        scrollStart,
        scrollEnd,
        mainMaxScrollLeft
      };

      this._updateSpacerHeight();
      this._collectItems();
      this._activeIndex = this._nearestIndex(this._main.scrollLeft);
      this._queueSync();
    };

    _updateSpacerHeight() {
      if (!this._main) return;
      const viewportH = window.innerHeight;
      const scrollableWidth = Math.max(0, this._main.scrollWidth - this._main.clientWidth);
      const requiredHeight = scrollableWidth + viewportH;

      if (Math.abs(this.offsetHeight - requiredHeight) > 1) {
        this.style.height = `${requiredHeight}px`;
        requestAnimationFrame(this._measure);
      }
    }

    // ===== Vertical → Horizontal mapping =====
    _onScroll() { this._queueSync(); }

    _queueSync() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._sync();
      });
    }

    _sync() {
      if (!this._main || this._isProgrammatic) return;
      // do not map while interacting/snap/animating to avoid fighting user input
      if (this._sessionActive || this._snapLock || this._animFrame) return;

      const rect = this.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const containerTop = scrollTop + rect.top;
      const containerHeight = this.offsetHeight;
      const viewportH = window.innerHeight;

      const scrollStart = containerTop;
      const scrollEnd = containerTop + containerHeight - viewportH;

      const max = Math.max(0, this._main.scrollWidth - this._main.clientWidth);
      if (max === 0) return;

      const y = scrollTop;
      if (y < scrollStart - CONFIG.PAD_PX || y > scrollEnd + CONFIG.PAD_PX) return;

      const progress = (y - scrollStart) / (scrollEnd - scrollStart);
      const clamped = Math.min(1, Math.max(0, progress));
      const targetLeft = clamped * max;

      if (Math.abs(this._main.scrollLeft - targetLeft) > CONFIG.EPS) {
        this._isProgrammatic = true;
        this._main.scrollLeft = targetLeft;
        requestAnimationFrame(() => (this._isProgrammatic = false));
      }
    }

    // ===== Wheel / Trackpad =====
    _onWheel(e) {
      if (!this._main) return;

      // Live sticky range
      const rect = this.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const containerTop = scrollTop + rect.top;
      const containerHeight = this.offsetHeight;
      const viewportH = window.innerHeight;

      const scrollStart = containerTop;
      const scrollEnd = containerTop + containerHeight - viewportH;

      const max = Math.max(0, this._main.scrollWidth - this._main.clientWidth);
      if (max === 0) return;

      const y = scrollTop;
      if (y < scrollStart || y > scrollEnd) return;

      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      let delta = absX > absY ? e.deltaX : e.deltaY;
      if (!delta) return;

      const isDiscrete =
        e.deltaMode === 1 || Math.abs(delta) >= CONFIG.WHEEL_DISCRETE_THRESHOLD;

      const left = this._main.scrollLeft;
      const canLeft  = left > CONFIG.EPS;
      const canRight = left < (max - CONFIG.EPS);

      // start a session if needed
      if (!this._sessionActive) {
        this._sessionActive = true;
        this._sessionStartLeft = left;
        this._sessionStartIndex = this._nearestIndex(left);
      }

      if (isDiscrete) {
        // one wheel tick => move exactly one slide
        const now = performance.now ? performance.now() : Date.now();
        if (now - this._lastWheelSnapTS < CONFIG.WHEEL_SNAP_COOLDOWN_MS) {
          e.preventDefault();
          return;
        }

        const dir = delta > 0 ? 1 : -1;
        const currentIndex = this._nearestIndex(this._main.scrollLeft);
        const lastIndex = Math.max(0, this._items.length - 1);

        // at boundary for the wheel direction -> allow vertical page scroll
        if ((dir > 0 && (currentIndex >= lastIndex || !canRight)) ||
            (dir < 0 && (currentIndex <= 0        || !canLeft ))) {
          this._sessionActive = false;
          return;
        }

        e.preventDefault();
        this._markUserHScroll();

        const targetIndex = Math.max(0, Math.min(currentIndex + dir, lastIndex));
        const targetLeft = (targetIndex === 0) ? 0 : this._leftOfIndex(targetIndex);
        const dur = (targetIndex === 0) ? CONFIG.SNAP_MS_FIRST : CONFIG.SNAP_MS;

        // rAF animation + vertical sync during the animation
        this._snapLock = true;
        this._isProgrammatic = true;
        this._animateScrollLeft(targetLeft, dur);

        // state
        this._activeIndex = targetIndex;
        this._lastWheelSnapTS = now;

        // end session shortly after the animation so mapping can resume
        setTimeout(() => {
          this._isProgrammatic = false;
          this._snapLock = false;
          this._sessionActive = false;
        }, dur + 20);

        return;
      }

      // Trackpad: smooth scrolling; only prevent default while horizontal movement is possible
      if ((delta < 0 && canLeft) || (delta > 0 && canRight)) {
        e.preventDefault();

        const next = Math.max(0, Math.min(left + delta, max));
        if (Math.abs(next - left) > CONFIG.EPS) {
          // cancel running animation to avoid fighting with user
          if (this._animFrame) cancelAnimationFrame(this._animFrame);
          this._animFrame = null;

          this._isProgrammatic = true;
          this._main.scrollLeft = next;
          requestAnimationFrame(() => (this._isProgrammatic = false));
          this._markUserHScroll();
        }

        const progress = next / max;
        const mappedY = scrollStart + progress * (scrollEnd - scrollStart);
        const clampedY = Math.max(scrollStart, Math.min(mappedY, scrollEnd));
        if (Math.abs(clampedY - y) > 0.5) window.scrollTo({ top: clampedY });

        clearTimeout(this._snapTimer);
        this._snapTimer = setTimeout(this._snapBySession, CONFIG.SNAP_DELAY_MS);
      }
      // at horizontal boundary -> let the page scroll vertically
    }

    // ===== Drag (Pointer) =====
    _onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      this._isDragging = true;
      this._dragStartX = e.clientX;
      this._dragStartScrollLeft = this._main.scrollLeft;

      this._sessionActive = true;
      this._sessionStartLeft = this._main.scrollLeft;
      this._sessionStartIndex = this._nearestIndex(this._sessionStartLeft);

      // stop any animation so drag feels direct
      if (this._animFrame) cancelAnimationFrame(this._animFrame);
      this._animFrame = null;

      this._main.setPointerCapture(e.pointerId);
      this._main.style.cursor = 'grabbing';

      this._main.addEventListener('pointermove', this._onPointerMove, { passive: true });
      this._main.addEventListener('pointerup', this._onPointerUp, { passive: true });
      this._main.addEventListener('pointercancel', this._onPointerUp, { passive: true });
    }

    _onPointerMove(e) {
      if (!this._isDragging) return;
      const dx = e.clientX - this._dragStartX;
      const target = this._dragStartScrollLeft - dx;
      const max = Math.max(0, this._main.scrollWidth - this._main.clientWidth);

      const clamped = Math.max(0, Math.min(target, max));
      if (Math.abs(this._main.scrollLeft - clamped) > CONFIG.EPS) {
        this._isProgrammatic = true;
        this._main.scrollLeft = clamped;
        requestAnimationFrame(() => (this._isProgrammatic = false));
        this._markUserHScroll();
      }
    }

    _onPointerUp(e) {
      this._isDragging = false;
      try { this._main.releasePointerCapture(e.pointerId); } catch {}
      this._main.style.cursor = 'grab';

      this._main.removeEventListener('pointermove', this._onPointerMove);
      this._main.removeEventListener('pointerup', this._onPointerUp);
      this._main.removeEventListener('pointercancel', this._onPointerUp);

      this._scheduleSnap();
    }

    // ===== Snap =====
    _scheduleSnap() {
      const now = performance.now ? performance.now() : Date.now();
      if (now - this._lastUserHScrollTS > CONFIG.USER_SNAP_WINDOW_MS) return;
      if (this._isProgrammatic || this._snapLock || this._animFrame) return;

      clearTimeout(this._snapTimer);
      this._snapTimer = setTimeout(this._snapBySession, CONFIG.SNAP_DELAY_MS);
    }

    _snapBySession() {
      if (!this._main) return;

      const vw = this._main.clientWidth || 1;
      const threshold = Math.max(CONFIG.THRESH_MIN_PX, vw * CONFIG.THRESH_RATIO);

      if (!this._sessionActive) {
        this._sessionStartLeft = this._main.scrollLeft;
        this._sessionStartIndex = this._nearestIndex(this._sessionStartLeft);
      }

      const currentLeft = this._main.scrollLeft;
      const delta = currentLeft - this._sessionStartLeft;

      let targetIndex = this._sessionStartIndex;
      if (Math.abs(delta) > threshold) targetIndex += (delta > 0 ? 1 : -1);
      targetIndex = Math.max(0, Math.min(targetIndex, Math.max(0, this._items.length - 1)));

      this._snapToIndex(targetIndex);
      this._sessionActive = false;
    }

    _snapToIndex(index) {
      // index 0 (first slide) always aligns to left edge 0 (no centering)
      const toLeft = index === 0 ? 0 : this._leftOfIndex(index);

      // close enough: set directly to avoid micro-jitter
      if (Math.abs(this._main.scrollLeft - toLeft) <= CONFIG.EPS) {
        this._isProgrammatic = true;
        this._main.scrollLeft = toLeft;
        requestAnimationFrame(() => (this._isProgrammatic = false));
        this._activeIndex = index;
        return toLeft;
      }

      // smooth rAF animation (+ slightly longer when returning to first slide)
      const dur = index === 0 ? CONFIG.SNAP_MS_FIRST : CONFIG.SNAP_MS;

      // cancel any running animation before starting a new one
      if (this._animFrame) cancelAnimationFrame(this._animFrame);
      this._animFrame = null;

      this._snapLock = true;
      this._isProgrammatic = true;
      this._animateScrollLeft(toLeft, dur);

      this._activeIndex = index;

      setTimeout(() => {
        this._isProgrammatic = false;
        this._snapLock = false;
      }, dur + 20);

      return toLeft;
    }
  }

  if (!customElements.get('scroll-sticky-container')) {
    customElements.define('scroll-sticky-container', ScrollStickyContainer);
  }
})();
